/**
 * Meybra Quick Development Update (V1)
 *
 * Lightweight workflow that NEVER replaces an athlete's full evaluation —
 * it only refines it. The coach writes one short observation, the AI returns
 * a conservative diff (max 3 changes), and only what the coach approves is
 * applied via the backend.
 *
 * Always rendered ALONGSIDE an existing evaluation. The parent page is in
 * charge of deciding whether the athlete already has an evaluation and
 * whether to expose this entry point.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, ArrowLeft, CheckCircle, XCircle, Edit3, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_LABEL = {
  ball_control: 'Ball Control', passing: 'Passing', dribbling: 'Dribbling',
  first_touch: 'First Touch', finishing: 'Finishing', shooting: 'Shooting',
  speed: 'Speed', acceleration: 'Acceleration', agility: 'Agility',
  coordination: 'Coordination', balance: 'Balance', explosiveness: 'Explosiveness',
  endurance: 'Endurance',
  positioning: 'Positioning', decision_making: 'Decision Making',
  vision: 'Vision', game_awareness: 'Game Awareness', reaction_time: 'Reaction Time',
};

const LEVELS = ['Focus', 'Developing', 'Proficient'];
const LEVEL_BADGE = {
  Focus: 'bg-red-500/20 text-red-400',
  Developing: 'bg-yellow-500/20 text-yellow-400',
  Proficient: 'bg-green-500/20 text-green-400',
};

const QuickUpdate = ({ athleteId, athleteName, coachId, currentEval, onApplied, onBack, initialObservation, autoAnalyze, source }) => {
  const { getAuthHeaders } = useAuth();
  const [observation, setObservation] = useState(initialObservation || '');
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [noChangeSummary, setNoChangeSummary] = useState('');
  const [proposed, setProposed] = useState([]);
  // editingIndex => allow inline edit of suggested_level / suggested_score
  const [decisions, setDecisions] = useState({}); // index -> { accept: bool, draft: {...} }
  const autoFiredRef = useRef(false);

  const analyze = async () => {
    if (observation.trim().length < 10) {
      toast.error('Write a bit more detail so the assistant can analyse it.');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API}/evaluations/athlete/${athleteId}/quick-update`,
        { observation: observation.trim(), source: source || null },
        { headers: getAuthHeaders() }
      );
      setUpdateId(res.data.update_id);
      setNoChangeSummary(res.data.no_change_summary || '');
      const list = res.data.proposed_changes || [];
      setProposed(list);
      // Default decision = accept everything the AI returned (coach can flip).
      const defaultDecisions = {};
      list.forEach((c, i) => {
        defaultDecisions[i] = {
          accept: c.type !== 'monitor',
          draft: {
            suggested_level: c.suggested_level,
            suggested_score: c.suggested_score,
            suggested_note: c.suggested_note,
            development_update: c.development_update || '',
          },
        };
      });
      setDecisions(defaultDecisions);
      if (!list.length) {
        toast.info('No changes recommended — observation logged for trend tracking.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || 'Failed to analyse observation.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Quick Capture entry point passes a pre-filled observation and asks us
  // to analyse it right away. We only fire once per mount.
  useEffect(() => {
    if (autoAnalyze && !autoFiredRef.current && observation.trim().length >= 10 && !proposed.length && !noChangeSummary) {
      autoFiredRef.current = true;
      analyze();
    }
  }, [autoAnalyze, athleteId]);

  const flipAccept = (i, accept) => {
    setDecisions((d) => ({ ...d, [i]: { ...d[i], accept } }));
  };

  const updateDraft = (i, field, value) => {
    setDecisions((d) => ({ ...d, [i]: { ...d[i], draft: { ...d[i].draft, [field]: value } } }));
  };

  const apply = async () => {
    const approved = proposed
      .map((c, i) => ({ idx: i, change: c, decision: decisions[i] }))
      .filter((x) => x.decision?.accept && x.change.type !== 'monitor')
      .map(({ change, decision }) => ({
        skill: change.skill,
        // The change is now a coordinated package (score + level + narrative
        // can all update together). Send everything the coach left in the
        // draft — backend ignores empty fields.
        type: change.type === 'monitor' ? 'note' : (change.type || 'package'),
        suggested_level: decision.draft.suggested_level || null,
        suggested_score:
          decision.draft.suggested_score === null || decision.draft.suggested_score === undefined || decision.draft.suggested_score === ''
            ? null
            : parseFloat(decision.draft.suggested_score),
        suggested_note: decision.draft.suggested_note || null,
        development_update: (decision.draft.development_update || '').trim() || null,
      }));

    if (!approved.length) {
      toast.info('Nothing approved — no changes applied.');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/evaluations/athlete/${athleteId}/quick-update/apply`,
        { update_id: updateId, approved_changes: approved, coach_id: coachId },
        { headers: getAuthHeaders() }
      );
      toast.success(`Applied ${res.data.count} change${res.data.count === 1 ? '' : 's'} — profile recompiling`, {
        action: { label: 'View Profile', onClick: () => onApplied?.() },
      });
      onApplied?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || 'Failed to apply changes.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/30 rounded-sm p-3 sm:p-4 mb-6 max-w-full overflow-hidden"
      data-testid="quick-update-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button onClick={onBack} className="text-muted-foreground hover:text-foreground shrink-0" aria-label="Back" data-testid="quick-update-back">
              <ArrowLeft size={18} />
            </button>
          )}
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
          <h3 className="font-heading text-sm uppercase tracking-wider truncate">Quick Development Update</h3>
          <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded shrink-0">V1</span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        Refine {athleteName || 'this athlete'}&apos;s existing evaluation with a single short note.
        The assistant suggests conservative changes — you stay in control.
      </p>

      {/* Current evaluation context — so the coach knows which baseline this update refines. */}
      {(currentEval?.last_updated_at || currentEval?.last_updated_by_name) && (
        <div
          className="bg-muted/20 border border-border/30 rounded-sm px-3 py-2 mb-3 text-[11px] text-muted-foreground"
          data-testid="quick-update-current-eval"
        >
          <p className="uppercase tracking-widest text-[9px] mb-0.5">Current Evaluation</p>
          {currentEval?.last_updated_at && (
            <p>
              Last updated:{' '}
              <span className="text-foreground font-semibold">
                {new Date(currentEval.last_updated_at).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric',
                })}
              </span>
            </p>
          )}
          {currentEval?.last_updated_by_name && (
            <p>By: <span className="text-foreground font-semibold">{currentEval.last_updated_by_name}</span></p>
          )}
        </div>
      )}

      {/* Step 1: Observation */}
      {!proposed.length && !noChangeSummary && (
        <div className="space-y-3" data-testid="quick-update-step-input">
          <Textarea
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            placeholder='e.g. "Over the last month her first touch has improved significantly. She is preparing the next action much faster, especially in tight spaces."'
            rows={4}
            className="bg-transparent w-full"
            disabled={analyzing}
            data-testid="quick-update-observation"
          />
          <Button
            onClick={analyze}
            disabled={analyzing || observation.trim().length < 10}
            className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-11"
            data-testid="quick-update-analyze"
          >
            {analyzing ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> Analysing observation</>
            ) : (
              <><Sparkles size={16} className="mr-2" /> Analyze observation</>
            )}
          </Button>
        </div>
      )}

      {/* Step 2: Review */}
      {(proposed.length > 0 || noChangeSummary) && (
        <div className="space-y-3" data-testid="quick-update-step-review">
          {noChangeSummary && proposed.length === 0 && (
            <div className="bg-muted/30 border border-border/40 p-3 rounded-sm">
              <p className="text-sm flex items-start gap-2">
                <MessageSquare size={14} className="text-primary mt-0.5 shrink-0" />
                <span>{noChangeSummary}</span>
              </p>
              <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                Observation saved for trend tracking — your full evaluation is unchanged.
              </p>
            </div>
          )}

          {proposed.map((c, i) => {
            const dec = decisions[i] || { accept: true, draft: {} };
            const isMonitor = c.type === 'monitor';
            return (
              <div
                key={i}
                className={`border rounded-sm p-3 space-y-2 transition-colors ${
                  isMonitor ? 'border-amber-500/30 bg-amber-500/5'
                  : dec.accept ? 'border-primary/40 bg-primary/5'
                  : 'border-border/40 bg-muted/20 opacity-60'
                }`}
                data-testid={`quick-update-change-${i}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-semibold text-sm uppercase tracking-wider">{SKILL_LABEL[c.skill] || c.skill}</p>
                  <div className="flex items-center gap-2">
                    {c.direction === 'up' && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-green-400 font-bold" data-testid={`quick-update-direction-up-${i}`}>
                        ▲ Improvement
                      </span>
                    )}
                    {c.direction === 'down' && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-400 font-bold" data-testid={`quick-update-direction-down-${i}`}>
                        ▼ Regression
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {c.type === 'monitor' ? 'Watch & monitor' : 'Profile evolution'}
                    </span>
                  </div>
                </div>

                {!isMonitor && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {/* Score */}
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Score</p>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-bold tabular-nums text-muted-foreground shrink-0">
                          {c.current_score ?? '—'}
                        </span>
                        <span className="text-muted-foreground shrink-0">→</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            disabled={!dec.accept}
                            onClick={() => {
                              const raw = parseFloat(dec.draft.suggested_score);
                              const base = Number.isFinite(raw) ? raw : (c.current_score ?? 5);
                              const next = Math.max(0, Math.min(10, base - 0.1));
                              updateDraft(i, 'suggested_score', next.toFixed(1));
                            }}
                            className="h-9 w-9 rounded border border-border/50 bg-muted/30 hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg leading-none active:scale-95 transition-transform shrink-0"
                            aria-label="Decrease score by 0.1"
                            data-testid={`quick-update-score-minus-${i}`}
                          >
                            −
                          </button>
                          <Input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*[.,]?[0-9]*"
                            placeholder="0.0"
                            value={dec.draft.suggested_score ?? ''}
                            onFocus={(e) => {
                              // Select all on focus so typing on mobile replaces
                              // the value instead of appending another digit to
                              // an existing decimal.
                              try { e.target.select(); } catch { /* noop */ }
                            }}
                            onChange={(e) => {
                              // Permissive mid-typing: keep digits, comma or dot,
                              // and AT MOST one separator. Do NOT truncate to one
                              // decimal here — that happens on blur. This lets
                              // users freely type "6", "6.", "6.4", or even
                              // overwrite "5.8" to "7" without the regex eating
                              // their keystrokes.
                              let v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                              const parts = v.split('.');
                              if (parts.length > 2) {
                                v = parts[0] + '.' + parts.slice(1).join('');
                              }
                              updateDraft(i, 'suggested_score', v);
                            }}
                            onBlur={(e) => {
                              // On blur: parse, clamp [0, 10], round to 1 decimal.
                              const v = e.target.value;
                              if (v === '' || v === '.') return;
                              const n = parseFloat(v);
                              if (Number.isNaN(n)) { updateDraft(i, 'suggested_score', ''); return; }
                              const clamped = Math.max(0, Math.min(10, n));
                              updateDraft(i, 'suggested_score', clamped.toFixed(1));
                            }}
                            disabled={!dec.accept}
                            className="h-9 w-16 text-sm bg-transparent tabular-nums text-center"
                            data-testid={`quick-update-score-${i}`}
                          />
                          <button
                            type="button"
                            disabled={!dec.accept}
                            onClick={() => {
                              const raw = parseFloat(dec.draft.suggested_score);
                              const base = Number.isFinite(raw) ? raw : (c.current_score ?? 5);
                              const next = Math.max(0, Math.min(10, base + 0.1));
                              updateDraft(i, 'suggested_score', next.toFixed(1));
                            }}
                            className="h-9 w-9 rounded border border-border/50 bg-muted/30 hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg leading-none active:scale-95 transition-transform shrink-0"
                            aria-label="Increase score by 0.1"
                            data-testid={`quick-update-score-plus-${i}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Level */}
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Level</p>
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${LEVEL_BADGE[c.current_level] || 'bg-muted'}`}>
                          {c.current_level || '—'}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <select
                          value={dec.draft.suggested_level || ''}
                          onChange={(e) => updateDraft(i, 'suggested_level', e.target.value || null)}
                          disabled={!dec.accept}
                          className="h-7 text-xs bg-transparent border border-border/50 rounded px-2"
                          data-testid={`quick-update-level-${i}`}
                        >
                          <option value="">(unchanged)</option>
                          {LEVELS.map((L) => <option key={L} value={L}>{L}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {!isMonitor && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Profile Update
                    </p>
                    {c.current_narrative && (
                      <p className="text-[11px] text-muted-foreground/70 italic mb-1.5 leading-relaxed line-clamp-3">
                        Current: {c.current_narrative}
                      </p>
                    )}
                    <Textarea
                      value={dec.draft.development_update || ''}
                      onChange={(e) => updateDraft(i, 'development_update', e.target.value)}
                      rows={3}
                      disabled={!dec.accept}
                      className="bg-transparent text-xs"
                      data-testid={`quick-update-narrative-${i}`}
                      placeholder="How the athlete now performs on the field…"
                    />
                  </div>
                )}

                {c.evidence_quote && (
                  <p className="text-[11px] text-muted-foreground italic leading-relaxed">
                    &ldquo;{c.evidence_quote}&rdquo;
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {c.reason || 'Based on coach observation and current athlete profile.'}
                </p>

                {!isMonitor && (
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => flipAccept(i, true)}
                      className={`flex-1 h-8 text-xs uppercase tracking-wider rounded transition-colors flex items-center justify-center gap-1 ${
                        dec.accept ? 'bg-primary text-black' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`quick-update-accept-${i}`}
                    >
                      <CheckCircle size={12} /> Accept
                    </button>
                    <button
                      onClick={() => flipAccept(i, false)}
                      className={`flex-1 h-8 text-xs uppercase tracking-wider rounded transition-colors flex items-center justify-center gap-1 ${
                        !dec.accept ? 'bg-red-500/30 text-red-400' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`quick-update-reject-${i}`}
                    >
                      <XCircle size={12} /> Reject
                    </button>
                  </div>
                )}
                {isMonitor && (
                  <p className="text-[11px] text-amber-400/80 leading-relaxed pt-1 flex items-center gap-1">
                    <Edit3 size={11} /> Profile unchanged — recorded so you can revisit next session.
                  </p>
                )}
              </div>
            );
          })}

          <Button
            onClick={apply}
            disabled={applying || proposed.every((_, i) => !decisions[i]?.accept) || proposed.every((c) => c.type === 'monitor')}
            className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-11"
            data-testid="quick-update-apply"
          >
            {applying ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> Applying</>
            ) : (
              <>Apply approved changes</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuickUpdate;
